import { Outlet, useLocation, Link } from 'react-router-dom';
import { useState, useEffect, useCallback, useRef } from 'react';
import './DashboardLayout.css';
import { formatMonthYear } from '../utils/dateUtils';
import { useBrand } from '../context/BrandContext';

// Optional one-line description shown under the page title in the header.
const PAGE_SUBTITLES = {
    '/dashboard': 'Manage your loan journey, payments and documents in one place.',
    '/dashboard/apply-loan': 'Get quick access to funds with a simple and secure process',
    '/dashboard/loans': 'View and manage all your loan applications in one place.',
    '/dashboard/documents': 'View and manage all your uploaded documents in one place.',
    '/dashboard/sanction-letter': 'Review your approved loan terms and complete Aadhaar eSign securely.',
    '/dashboard/profile': 'Manage your personal information and preferences',
    '/dashboard/kyc': 'Complete your KYC to unlock all features.',
    '/dashboard/support': "Need help? We're just a message away.",
};

// Session timeout settings
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes

function DashboardLayout() {
    const { logoUrl: LOGO_URL, logoAlt: LOGO_ALT, brandName: BRAND_NAME } = useBrand();
    const location = useLocation();
    const [showLogoutWarning, setShowLogoutWarning] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [isSidebarMobileOpen, setIsSidebarMobileOpen] = useState(false);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    // Current step of a multi-step page (apply loan), shown in the header.
    const [headerStage, setHeaderStage] = useState(null);
    const [, setHasActiveLoan] = useState(false);
    const [, setLoanStats] = useState({ active: 0, pending: 0 });
    const lastActivityRef = useRef(Date.now());
    const notificationsRef = useRef(null);
    const profileRef = useRef(null);

    const [notifications] = useState([
        {
            id: 1,
            type: 'payment',
            title: 'EMI Due Soon',
            message: 'Your Personal Loan EMI of ₹15,234 is due on 15 Dec 2025',
            time: '2 hours ago',
            read: false
        },
        {
            id: 2,
            type: 'success',
            title: 'Loan Application Approved',
            message: 'Your Two-Wheeler loan application has been approved!',
            time: '1 day ago',
            read: false
        },
        {
            id: 3,
            type: 'info',
            title: 'Credit Score Updated',
            message: 'Your credit score increased by 12 points this month',
            time: '2 days ago',
            read: true
        }
    ]);

    const getSessionUser = () => {
        try {
            return JSON.parse(localStorage.getItem('userSession')) || {};
        } catch {
            return {};
        }
    };

    const hasLoanTaken = getSessionUser().hasLoanTaken === true;

    // Documents still requested on the current application. Seeded from the
    // login response (session.docsPendingCount) and refreshed on every route
    // change so the badge drops as soon as the customer uploads.
    const [docsPending, setDocsPending] = useState(() => {
        const n = Number(getSessionUser().docsPendingCount);
        return Number.isFinite(n) && n > 0 ? n : 0;
    });

    const [user, setUser] = useState({
        name: getSessionUser().name || 'Loading...',
        email: '',
        phone: getSessionUser().phone || '',
        joinDate: '',
        kycStatus: 'Pending',
        accountStatus: 'Active',
        role: getSessionUser().role || 'user'
    });

    const API_URL = process.env.REACT_APP_API_URL || '';
    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession'));
        console.log('🏗️ [FRONTEND-DASHBOARD] Initializing layout, session found:', !!session);
        if (session && session.userId) {
            const fetchUrl = `${API_URL}/api/auth/profile/${session.userId}`;
            console.log(`🌐 [FRONTEND-DASHBOARD] Fetching profile: ${fetchUrl}`);
            fetch(fetchUrl)
                .then(res => {
                    console.log(`📡 [FRONTEND-DASHBOARD] Profile response status: ${res.status}`);
                    return res.json();
                })
                .then(data => {
                    console.log('📦 [FRONTEND-DASHBOARD] Profile data:', data);
                    if (data.success) {
                        setUser(prev => ({
                            ...prev,
                            name: data.user.name || prev.name,
                            email: data.user.personal_email || data.user.email || prev.email,
                            phone: data.user.phone || prev.phone,
                            joinDate: formatMonthYear(data.user.joinDate),
                            kycStatus: data.user.kycStatus || 'In Progress',
                            accountStatus: 'Active',
                            role: data.user.role || prev.role || 'user'
                        }));
                    }
                })
                .catch(err => {
                    console.error('❌ [FRONTEND-DASHBOARD] Profile fetch error:', err);
                    setUser(prev => ({ ...prev, name: prev.name || 'Loading...' }));
                });
        }
    }, [API_URL]);

    // Keep the Documents badge current: re-count pending documents on every
    // navigation and whenever the session changes (login / upload flows).
    useEffect(() => {
        let cancelled = false;
        const refresh = async () => {
            const session = getSessionUser();
            if (!session?.token) return;
            try {
                const res = await fetch(`${API_URL}/api/documents/required`, {
                    headers: { Authorization: `Bearer ${session.token}` },
                    credentials: 'include',
                });
                const data = await res.json().catch(() => ({}));
                if (cancelled || !res.ok || !data.success) return;
                const count = Array.isArray(data.pending) ? data.pending.length : 0;
                setDocsPending(count);
                if (session.docsPendingCount !== count) {
                    try {
                        localStorage.setItem('userSession', JSON.stringify({ ...session, docsPendingCount: count }));
                    } catch { /* storage unavailable: badge still updates in memory */ }
                }
            } catch { /* network error: keep the last known count */ }
        };
        refresh();
        window.addEventListener('userSessionChanged', refresh);
        return () => {
            cancelled = true;
            window.removeEventListener('userSessionChanged', refresh);
        };
    }, [API_URL, location.pathname]);

    useEffect(() => {
        const session = JSON.parse(localStorage.getItem('userSession') || 'null');
        if (!session?.userId) return;
        fetch(`${API_URL}/api/dashboard/all-data/${session.userId}`, {
            headers: session.token ? { Authorization: `Bearer ${session.token}` } : undefined
        })
            .then((res) => res.json())
            .then((data) => {
                if (data?.success) {
                    setHasActiveLoan(Number(data?.stats?.activeLoans || 0) > 0);
                    setLoanStats({
                        active: Number(data?.stats?.activeLoans || 0),
                        pending: Number(data?.stats?.pendingLoans || 0),
                        adminApproved: Number(data?.stats?.adminApprovedLoans || 0)
                    });
                }
            })
            .catch(() => {});
    }, [API_URL]);

    // SVG icon map
    const icons = {
        dashboard: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>),
        loans:    (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>),
        payments: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>),
        pay:      (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><path d="M15 9.5c-.5-.7-1.5-1.1-2.7-1.1-1.6 0-2.8.8-2.8 2 0 3 5.6 1.3 5.6 4.1 0 1.2-1.2 2-2.8 2-1.3 0-2.4-.5-3-1.3"/><path d="M12 7v10"/></svg>),
        documents:(<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>),
        sanction: (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><path d="m9 15 2 2 4-4"/></svg>),
        support:  (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>),
        profile:  (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>),
        kyc:      (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/></svg>),
        shield:   (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>),
        folder:   (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>),
        apply:    (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>),
        logout:   (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>),
    };

const navGroups = [
        {
            label: 'Main',
            links: [
                { path: '/dashboard',             label: 'Dashboard',      icon: icons.dashboard },
                { path: '/dashboard/apply-loan',  label: 'Apply for Loan', icon: icons.apply, highlight: true },
                // { path: '/dashboard/pay',         label: 'Pay',            icon: icons.pay },
                { path: '/dashboard/loans',       label: 'My Loans',       icon: icons.loans },
                ...(hasLoanTaken ? [
                    { path: '/dashboard/payments', label: 'Payments',       icon: icons.payments },
                ] : []),
                { path: '/dashboard/documents',   label: 'Documents',      icon: icons.documents, badge: docsPending },
                { path: '/dashboard/sanction-letter', label: 'Sanction Letter', icon: icons.sanction },
            ]
        },
        {
            label: 'Account',
            links: [
                { path: '/dashboard/profile',    label: 'My Profile',      icon: icons.profile },
                { path: '/dashboard/kyc',        label: 'KYC',             icon: icons.kyc },
                { path: '/dashboard/support',    label: 'Support',         icon: icons.support },
            ]
        },
];


const getUserInitials = (name) => {
    if (!name || name === 'Loading...') return 'K';
    const initials = name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase();
    return initials || 'K';
};

const capitalizeName = (name) => {
    const value = String(name || '').trim();
    if (!value || value === 'Loading...') return value;
    return value
        .split(' ')
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

    const adminGroup = {
        label: 'Admin',
        links: [
            { path: '/dashboard/admin/loans', label: 'Manage Loans', icon: icons.shield },
        ]
    };

    const isAdmin = user.role === 'admin' || user.role === 'super_admin';
    const allGroups = isAdmin ? [...navGroups, adminGroup] : navGroups;

    // Close the mobile drawer whenever the route changes, however the
    // navigation happened (sidebar link, bottom nav, in-page button).
    useEffect(() => {
        setIsSidebarMobileOpen(false);
    }, [location.pathname]);

    // Escape closes the mobile drawer.
    useEffect(() => {
        if (!isSidebarMobileOpen) return undefined;
        const onKey = (event) => {
            if (event.key === 'Escape') setIsSidebarMobileOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [isSidebarMobileOpen]);

    // Handle logout
    const handleLogout = useCallback(() => {
        // Save profile picture before clearing
        const profilePic = localStorage.getItem('profilePicture');

        localStorage.clear();
        sessionStorage.clear();

        // Restore profile picture
        if (profilePic) {
            localStorage.setItem('profilePicture', profilePic);
        }

        document.cookie.split(";").forEach((c) => {
            document.cookie = c
                .replace(/^ +/, "")
                .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
        });
        window.history.pushState(null, '', '/login');
        window.onpopstate = function () {
            window.history.pushState(null, '', '/login');
        };
        window.location.replace('/login');
    }, []);

    // Track activity and timeouts
    useEffect(() => {
        const updateActivity = () => {
            lastActivityRef.current = Date.now();
        };
        const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'touchmove', 'pointerdown', 'mousemove'];
        events.forEach(event => window.addEventListener(event, updateActivity, { passive: true }));

        const checkTimeout = setInterval(() => {
            const now = Date.now();
            if (now - lastActivityRef.current >= INACTIVITY_TIMEOUT) {
                alert('Logged out due to inactivity.');
                handleLogout();
            }
        }, 60000);

        return () => {
            events.forEach(event => window.removeEventListener(event, updateActivity));
            clearInterval(checkTimeout);
        };
    }, [handleLogout]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
                setShowNotifications(false);
            }

            if (showProfile && profileRef.current && !profileRef.current.contains(event.target)) {
                setShowProfile(false);
            }
        };

        // Use both mousedown and touchstart for iOS Safari
        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('touchstart', handleOutsideClick, { passive: true });

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [showNotifications, showProfile]);

    const confirmLogout = () => {
        setShowLogoutWarning(false);
        handleLogout();
    };

    const unreadCount = notifications.filter(n => !n.read).length;
    return (
        <div className="dashboard-container">
            {/* ── Sidebar ── */}
            <aside className={`dashboard-sidebar ${isSidebarMobileOpen ? 'mobile-open' : ''} ${isSidebarCollapsed ? 'collapsed' : ''}`}>

                {/* Brand */}
                <div className="sidebar-brand">
                    <div className="sidebar-brand-id">
                        <img src={LOGO_URL} alt={LOGO_ALT} className="sidebar-logo" />
                        <div className="sidebar-brand-text">
                            <span className="sidebar-brand-name">{BRAND_NAME}</span>
                            <span className="sidebar-brand-tagline">Secure Today, Smarter Tomorrow</span>
                        </div>
                    </div>
                    <button
                        className="collapse-toggle"
                        onClick={() => {
                            // On mobile this button closes the drawer instead of collapsing it.
                            if (window.matchMedia('(max-width: 768px)').matches) {
                                setIsSidebarMobileOpen(false);
                            } else {
                                setIsSidebarCollapsed(!isSidebarCollapsed);
                            }
                        }}
                        title={isSidebarMobileOpen ? 'Close menu' : isSidebarCollapsed ? 'Expand' : 'Collapse'}
                        aria-label={isSidebarMobileOpen ? 'Close menu' : isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            {isSidebarCollapsed
                                ? <polyline points="9 18 15 12 9 6" />
                                : <polyline points="15 18 9 12 15 6" />}
                        </svg>
                    </button>
                </div>

                {/* <div className="sidebar-quest-card">
                    <div className="quest-card-top">
                        <span className="quest-chip">Daily Quest</span>
                        <span className="quest-rank">Lv. {gamifiedProfile.level}</span>
                    </div>
                    <h3>Keep the streak alive</h3>
                    <p>Check your loans, finish KYC, and grab rewards as you move up.</p>
                    <div className="quest-progress">
                        <div className="quest-progress-track">
                            <div className="quest-progress-fill" style={{ width: `${xpProgress}%` }} />
                        </div>
                        <div className="quest-progress-meta">
                            <span>{gamifiedProfile.xp} XP</span>
                            <span>Next reward: {gamifiedProfile.nextReward}</span>
                        </div>
                    </div>
                    <div className="quest-stats">
                        <div>
                            <strong>{gamifiedProfile.streak}</strong>
                            <span>Day streak</span>
                        </div>
                        <div>
                            <strong>{gamifiedProfile.rank}</strong>
                            <span>Current rank</span>
                        </div>
                    </div>
                </div> */}

                {/* Grouped Nav */}
                <nav className="sidebar-nav">
                    {allGroups.map((group) => (
                        <div key={group.label} className="nav-group">
                            <span className="nav-group-label">{group.label}</span>
                            {group.links.map((link) => {
                                const isActive = location.pathname === link.path;
                                return (
                                    <Link
                                        key={link.path}
                                        to={link.path}
                                        className={`nav-item ${isActive ? 'active nav-highlight' : ''}`}
                                        onClick={() => setIsSidebarMobileOpen(false)}
                                        title={isSidebarCollapsed ? link.label : undefined}
                                    >
                                        <span className="nav-icon">{link.icon}</span>
                                        <span className="nav-label">{link.label}</span>
                                        {link.badge > 0 && (
                                            <span
                                                className="nav-count"
                                                aria-label={`${link.badge} pending`}
                                                title={`${link.badge} document${link.badge === 1 ? '' : 's'} pending`}
                                            >
                                                {link.badge > 9 ? '9+' : link.badge}
                                            </span>
                                        )}
                                        {isActive && <span className="nav-pip" />}
                                    </Link>
                                );
                            })}
                        </div>
                    ))}
                </nav>

                {/* User card + Logout */}
                <div className="sidebar-footer">
                    <div className="sidebar-help-card">
                        <div className="sidebar-help-art" aria-hidden="true">🪴</div>
                        <strong>Need Help?</strong>
                        <span>Our team is here for you</span>
                        <Link
                            to="/dashboard/support"
                            className="sidebar-help-btn"
                            onClick={() => setIsSidebarMobileOpen(false)}
                            title={isSidebarCollapsed ? 'Contact Support' : undefined}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>
                            <span className="nav-label">Contact Support</span>
                        </Link>
                    </div>
                    {/* <div className="sidebar-user-card">
                        <div className="sidebar-avatar">{getUserInitials(user.name)}</div>
                        <div className="sidebar-user-info">
                            <span className="sidebar-user-name">{capitalizeName(user.name) || 'Loading...'}</span>
                            <span className="sidebar-user-role">{getRoleLabel(user.role)}</span>
                        </div>
                    </div> */}
                    {/* <button className="sidebar-logout-btn" onClick={() => setShowLogoutWarning(true)} title={isSidebarCollapsed ? 'Logout' : undefined}>
                        <span className="nav-icon">{icons.logout}</span>
                        <span className="nav-label">Sign Out</span>
                    </button> */}
                </div>
            </aside>

            {/* Main Content Area */}
            <div className={`dashboard-wrapper ${isSidebarCollapsed ? 'collapsed' : ''}`}>
                {/* Top Header */}
                <header className="dashboard-header-new">
                    <div className="header-left">
                        <button className="mobile-toggle" aria-label="Open menu" aria-expanded={isSidebarMobileOpen} onClick={() => setIsSidebarMobileOpen(!isSidebarMobileOpen)}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h10" /></svg>
                        </button>
                        <div className="page-title-wrap">
                            <h2 className="page-title">
                                {allGroups.flatMap(g => g.links).find(l => l.path === location.pathname)?.label || 'Dashboard'}
                            </h2>
                            {headerStage ? (
                                <p className="page-stage">
                                    <span className="page-stage-badge">Stage {headerStage.step} of {headerStage.total}</span>
                                    <span className="page-stage-title"><span aria-hidden="true">{headerStage.icon}</span> {headerStage.title}</span>
                                    {headerStage.desc && <span className="page-stage-desc">{headerStage.desc}</span>}
                                </p>
                            ) : PAGE_SUBTITLES[location.pathname] && (
                                <p className="page-subtitle">{PAGE_SUBTITLES[location.pathname]}</p>
                            )}
                        </div>
                    </div>

                    <div className="header-right">
                        <div className="header-actions">
                            <div ref={notificationsRef}>
                                <button className="header-btn" onClick={() => {
                                    setShowNotifications(!showNotifications);
                                    setShowProfile(false);
                                }}>
                                    <span className="icon"><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 22a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 22zm7-6v-5a7 7 0 0 0-5.5-6.84V3.5a1.5 1.5 0 0 0-3 0v.66A7 7 0 0 0 5 11v5l-1.7 1.7a.75.75 0 0 0 .53 1.3h16.34a.75.75 0 0 0 .53-1.3z" /></svg></span>
                                    {unreadCount > 0 && <span className="badge">{unreadCount}</span>}
                                </button>

                                {showNotifications && (
                                    <div className="header-dropdown notifications-dropdown">
                                        <div className="dropdown-header">
                                            <h3>Notifications</h3>
                                            <button className="clear-all">Clear All</button>
                                        </div>
                                        <div className="dropdown-list">
                                            {notifications.map(n => (
                                                <div key={n.id} className={`dropdown-item ${n.read ? 'read' : 'unread'}`}>
                                                    <div className="item-icon">{n.type === 'payment' ? '💳' : n.type === 'success' ? '✅' : 'ℹ️'}</div>
                                                    <div className="item-body">
                                                        <h4>{n.title}</h4>
                                                        <p>{n.message}</p>
                                                        <span className="time">{n.time}</span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div ref={profileRef}>
                                <button className={`header-profile-btn ${showProfile ? 'open' : ''}`} onClick={() => {
                                    setShowProfile(!showProfile);
                                    setShowNotifications(false);
                                }}>
                                    <div className="avatar">
                                        {getUserInitials(user.name)}
                                    </div>
                                    <div className="name-info">
                                        <span className="user-name">{capitalizeName(user.name)}</span>
                                    </div>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="profile-chevron">
                                        <polyline points="6 9 12 15 18 9"></polyline>
                                    </svg>
                                </button>
                                {showProfile && (
                                    <div className="header-dropdown profile-dropdown">
                                        <div className="profile-top">
                                            <div className="avatar-large">
                                                {user.name && user.name !== 'Loading...'
                                                    ? user.name.split(' ').map(n => n[0]).filter(Boolean).join('').toUpperCase()
                                                    : '🌙'}
                                            </div>
                                            <h4>{user.name}</h4>
                                            <p>{user.email}</p>
                                        </div>
                                        <div className="profile-menu">
                                            <Link to="/dashboard/profile" className="menu-item" onClick={() => setShowProfile(false)}>
                                                👤 My Profile
                                            </Link>
                                            <button className="menu-item" onClick={() => setShowLogoutWarning(true)}>
                                                🚪 Logout
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </header>

                <main className="dashboard-content-new">
                    <Outlet context={{ setHeaderStage }} />
                </main>

                {/* Mobile Bottom Navigation */}
                <div className="mobile-bottom-nav">
                    <Link to="/dashboard" className={`bottom-nav-item ${location.pathname === '/dashboard' ? 'active' : ''}`}>
                        <span className="nav-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3 3 10.5V20a1 1 0 0 0 1 1h5.5v-6h5v6H20a1 1 0 0 0 1-1v-9.5z" /></svg></span>
                        <span>Home</span>
                    </Link>
                    <Link to="/dashboard/loans" className={`bottom-nav-item ${location.pathname === '/dashboard/loans' ? 'active' : ''}`}>
                        <span className="nav-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M21 7H5.5a1.5 1.5 0 0 1 0-3H19V2.5A.5.5 0 0 0 18.5 2H5.5A3.5 3.5 0 0 0 2 5.5v13A3.5 3.5 0 0 0 5.5 22H21a1 1 0 0 0 1-1V8a1 1 0 0 0-1-1zm-3.5 9a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" /></svg></span>
                        <span>Loans</span>
                    </Link>
                    <Link to="/dashboard/payments" className={`bottom-nav-item ${location.pathname === '/dashboard/payments' ? 'active' : ''}`}>
                        <span className="nav-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 4H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2zm0 14H4v-6h16zm0-10H4V6h16z" /></svg></span>
                        <span>Pay</span>
                    </Link>
                    <Link to="/dashboard/support" className={`bottom-nav-item ${location.pathname === '/dashboard/support' ? 'active' : ''}`}>
                        <span className="nav-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2zM7 9h10v2H7zm6 5H7v-2h6zm4-6H7V6h10z" /></svg></span>
                        <span>Help</span>
                    </Link>
                </div>
            </div>

            {/* Overlay for mobile sidebar */}
            {isSidebarMobileOpen && <div className="sidebar-overlay" onClick={() => setIsSidebarMobileOpen(false)}></div>}

            {/* Logout Warning Modal */}
            {showLogoutWarning && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-icon warning">⚠️</div>
                        <h3>Confirm Logout</h3>
                        <p>Are you sure you want to log out of your dashboard?</p>
                        <div className="modal-actions">
                            <button className="modal-btn cancel" onClick={() => setShowLogoutWarning(false)}>
                                Cancel
                            </button>
                            <button className="modal-btn confirm" onClick={confirmLogout}>
                                Yes, Logout
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

export default DashboardLayout;
